import { useEffect, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { Link2Off, Loader2 } from 'lucide-react';

import { useResolveConversationShareQuery } from '@/api/endpoints/conversation-share';
import { SharedConversationView } from '@/components/features/conversation/share/SharedConversationView';

const SHARE_TOKEN_STORAGE_KEY = 'tessera.share.token';
// Share tokens are `secrets.token_urlsafe(...)` → base64url, so they only ever contain these
// characters. Anything else in the fragment (e.g. a mis-routed OAuth `#access_token=…&…`) is
// rejected rather than stashed, so a stray hash can't pin the tab to "invalid or expired".
const SHARE_TOKEN_PATTERN = /^[A-Za-z0-9_-]{1,512}$/;

export const Route = createFileRoute('/_protected/share')({
  component: SharePage,
});

/**
 * Read the share token from the URL fragment. The fragment never reaches the
 * server (so it stays out of access logs), but it can also be dropped by the
 * Keycloak login redirect — so we stash it in sessionStorage on first sight
 * and fall back to the stash when the hash is empty after returning from auth.
 */
function readShareToken(): string | null {
  if (typeof window === 'undefined') return null;

  const fromHash = window.location.hash.startsWith('#') ? window.location.hash.slice(1) : '';
  if (fromHash) {
    if (!SHARE_TOKEN_PATTERN.test(fromHash)) return null;
    sessionStorage.setItem(SHARE_TOKEN_STORAGE_KEY, fromHash);
    return fromHash;
  }
  return sessionStorage.getItem(SHARE_TOKEN_STORAGE_KEY);
}

function SharePage() {
  const [token, setToken] = useState<string | null>(null);
  const [isReadingToken, setIsReadingToken] = useState(true);

  useEffect(() => {
    setToken(readShareToken());
    setIsReadingToken(false);
  }, []);

  const { data: share, isLoading, isError } = useResolveConversationShareQuery(token);

  // Clear the stash once the token has served its purpose — on success OR failure — so a stale
  // or invalid token can't outlive this resolve attempt and pin a later visit.
  useEffect(() => {
    if (share || isError) sessionStorage.removeItem(SHARE_TOKEN_STORAGE_KEY);
  }, [share, isError]);

  if (isReadingToken || (token && isLoading)) {
    return (
      <div className="flex h-full items-center justify-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="size-4 animate-spin" />
        Opening shared conversation…
      </div>
    );
  }

  if (!token || isError || !share) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3 px-4 text-center">
        <Link2Off className="size-8 text-muted-foreground" />
        <span className="text-sm font-semibold text-white">This share link is invalid or has expired</span>
        <span className="max-w-md text-xs text-muted-foreground">
          Ask the person who shared it to create a new link — share links expire automatically and can be revoked by
          their owner.
        </span>
      </div>
    );
  }

  return <SharedConversationView share={share} token={token} />;
}
